import React, { useEffect, useState } from 'react';
import {
	Card,
	CardContent,
	Grid,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import { AgentCostReport, AgentCostRow, getCosts } from '../../Services/agents';

// Ce que les agents coûtent sur 30 jours, en séparant ce qui ne se compare pas :
// l'ANALYSE (récurrente : c'est elle qui fait la facture mensuelle d'un agent), le PLAN
// (ponctuel et cher) et la SYNTHÈSE des managers. Les dollars sont des ESTIMATIONS
// (tarifs réglables par env sur api-agents) ; la facture réelle est dans la console
// Anthropic et dans la facturation GCP du projet qui exécute la requête.

const fmt = (n: number) => n.toLocaleString('fr-FR');
const usd = (n: number) =>
	`${n.toLocaleString('fr-FR', { minimumFractionDigits: n < 1 ? 4 : 2, maximumFractionDigits: n < 1 ? 4 : 2 })} $`;
const mib = (b: number) =>
	`${(b / 1024 ** 2).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mio`;

const KIND_LABEL: Record<string, { label: string; hint: string }> = {
	run: {
		label: 'Analyses (récurrent)',
		hint: "Exécution quotidienne/hebdo du plan : SQL + un appel LLM de rédaction, seulement s'il y a du nouveau. C'est la facture mensuelle d'un agent.",
	},
	plan: {
		label: 'Plans (ponctuel)',
		hint: "Session outillée à l'activation, au changement de sources ou sur « Plan now ». Cher à l'unité, rare. Les requêtes d'exploration du modèle ne sont pas comptées.",
	},
	synthesis: {
		label: 'Synthèses (managers)',
		hint: 'Un appel LLM par manager et par période, sans accès aux données.',
	},
	briefing: {
		label: 'Points personnels',
		hint: "Un appel LLM par membre et par jour au plus (« Your briefing ») : le seul coût qui croît avec le nombre de membres.",
	},
	sources: {
		label: 'Sources à brancher',
		hint: 'Fusion des sources manquantes signalées par tous les plans du projet : un appel par vague de plans.',
	},
};

// #region KindTile
const KindTile: React.FC<{ kind: string; row?: AgentCostRow }> = ({
	kind,
	row,
}) => {
	const meta = KIND_LABEL[kind];
	return (
		<Grid item xs={12} md={4} sx={{ display: 'flex' }}>
			<Tooltip title={meta.hint}>
				<Card sx={{ width: '100%' }}>
					<CardContent>
						<Typography variant="caption" color="text.secondary">
							{meta.label}
						</Typography>
						<Typography variant="h5">{usd(row?.total_usd || 0)}</Typography>
						<Typography variant="body2" color="text.secondary">
							{fmt(row?.count || 0)} passage(s) · {usd(row?.avg_usd || 0)} en
							moyenne
						</Typography>
						<Typography
							variant="caption"
							color="text.secondary"
							display="block"
						>
							LLM {usd(row?.llm_usd || 0)} · BigQuery {usd(row?.bq_usd || 0)} (
							{mib(row?.bytes_billed || row?.bytes_processed || 0)} facturés)
						</Typography>
					</CardContent>
				</Card>
			</Tooltip>
		</Grid>
	);
};
// #endregion

// #region AgentCostTiles
export const AgentCostTiles: React.FC = () => {
	const [byKind, setByKind] = useState<AgentCostReport | null>(null);
	const [byRole, setByRole] = useState<AgentCostReport | null>(null);
	useEffect(() => {
		getCosts('kind')
			.then(setByKind)
			.catch(() => setByKind(null));
		getCosts('role')
			.then(setByRole)
			.catch(() => setByRole(null));
	}, []);
	if (!byKind) return null;
	const kind = (k: string) => byKind.rows.find((r) => r.key === k);
	const p = byKind.pricing;
	return (
		<>
			<Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
				Coûts estimés des 30 derniers jours · total{' '}
				{usd(byKind.total.total_usd)}
			</Typography>
			<Grid container spacing={2} alignItems="stretch">
				{['run', 'plan', 'synthesis', 'briefing', 'sources'].map((k) => (
					<KindTile key={k} kind={k} row={kind(k)} />
				))}
			</Grid>
			<Typography
				variant="caption"
				color="text.secondary"
				display="block"
				sx={{ mt: 1 }}
			>
				Tarifs d'estimation : {p.input_usd_per_mtok} $ / {p.output_usd_per_mtok}{' '}
				$ par million de tokens (entrée / sortie), {p.bq_usd_per_tib} $ par Tio
				BigQuery facturé.
			</Typography>
			{byRole && byRole.rows.length > 0 && <RoleTable report={byRole} />}
		</>
	);
};
// #endregion

// #region RoleTable
const RoleTable: React.FC<{ report: AgentCostReport }> = ({ report }) => (
	<Card sx={{ mt: 2 }}>
		<Table size="small">
			<TableHead>
				<TableRow>
					<TableCell>Rôle</TableCell>
					<TableCell align="right">Plans</TableCell>
					<TableCell align="right">Analyses</TableCell>
					<TableCell align="right">Erreurs</TableCell>
					<TableCell align="right">Tokens in / out</TableCell>
					<TableCell align="right">BigQuery facturé</TableCell>
					<TableCell align="right">LLM</TableCell>
					<TableCell align="right">BigQuery</TableCell>
					<TableCell align="right">Total</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{report.rows.map((r) => (
					<TableRow key={r.key}>
						<TableCell>{r.key}</TableCell>
						<TableCell align="right">{fmt(r.plans)}</TableCell>
						<TableCell align="right">{fmt(r.runs)}</TableCell>
						<TableCell align="right">{fmt(r.errors)}</TableCell>
						<TableCell align="right">{`${fmt(r.llm_input_tokens)}${r.llm_cache_read_tokens ? ` (+${fmt(r.llm_cache_read_tokens)} en cache)` : ''} / ${fmt(r.llm_output_tokens)}`}</TableCell>
						<TableCell align="right">
							{mib(r.bytes_billed || r.bytes_processed)}
						</TableCell>
						<TableCell align="right">{usd(r.llm_usd)}</TableCell>
						<TableCell align="right">{usd(r.bq_usd)}</TableCell>
						<TableCell align="right">{usd(r.total_usd)}</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	</Card>
);
// #endregion
